import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Award, Users, TrendingUp, Shield } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2V8ZW58MHx8fHwxNzcxNTc4NTkwfDA&ixlib=rb-4.1.0&q=85"
            alt="Office"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-[#1B5E20]/90 to-[#0D3D13]/85"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-[white]/10 backdrop-blur-sm border border-[white]/30 rounded-full">
              <span className="text-[white] font-medium tracking-wide">Est. 1991 | 35 Years of Excellence</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white leading-tight">
              S.K. Mundhra & Co.
            </h1>
            
            <p className="text-2xl md:text-3xl text-[white] mb-4 font-light">
              Chartered Accountants
            </p>
            
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Based in Rourkela, Odisha, over three decades of disciplined practice, delivering audit and assurance services with unwavering clarity and integrity
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-white text-[#1B5E20] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-xl transition-all duration-300 transform hover:scale-105 font-semibold"
              >
                Schedule a Meeting
              </Button>
              <a 
                href="https://wa.me/919437046671?text=Hello%20S.K.%20Mundhra%20%26%20Co."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg backdrop-blur-sm transition-all duration-300"
                >
                  WhatsApp Us
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">35+</div>
                <div className="text-gray-300 text-sm md:text-base">Years of Legacy</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">20+</div>
                <div className="text-gray-300 text-sm md:text-base">Team Members</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">3</div>
                <div className="text-gray-300 text-sm md:text-base">CA Partners</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">100%</div>
                <div className="text-gray-300 text-sm md:text-base">Client Trust</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Firm Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-green-900/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                A Legacy of Trust & Excellence
              </h2>
              <div className="w-24 h-1 bg-#1B5E20 mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NzE1Nzg1ODV8MA&ixlib=rb-4.1.0&q=85"
                    alt="Business consultation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-#1B5E20/10 rounded-2xl backdrop-blur-sm border border-white/20 -z-10"></div>
              </div>

              <div className="space-y-6">
                <p className="text-lg text-slate-700 leading-relaxed">
                  Based in Rourkela, Odisha, <span className="font-semibold text-#0D3D13">S.K. Mundhra & Co.</span> has established a disciplined and trusted practice over three decades under the leadership of CA S.K. Mundhra, delivering audit and assurance services with clarity and integrity.
                </p>
                <p className="text-lg text-slate-700 leading-relaxed">
                  The firm comprises <span className="font-semibold text-#0D3D13">three Chartered Accountant partners</span>, supported by two qualified Chartered Accountants within the professional team, bringing the total strength to <span className="font-semibold text-#0D3D13">over 20 members</span>.
                </p>
                <p className="text-lg text-slate-700 leading-relaxed">
                  The continuity of the practice is reflected in the long-standing association of several team members who have been with the firm for over two decades, combining experience with the fresh perspectives of newly qualified professionals.
                </p>

                <div className="grid grid-cols-2 gap-4 pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Award className="h-6 w-6 text-#0D3D13" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Ethical Practice</div>
                      <div className="text-sm text-slate-600">Unwavering integrity</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="h-6 w-6 text-#0D3D13" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Expert Team</div>
                      <div className="text-sm text-slate-600">20+ professionals</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-#1B5E20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-5 gap-12 items-center">
              <div className="md:col-span-2">
                <div className="relative">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/30">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_ca-firm-site/artifacts/u3e48mwe_24655.jpg"
                      alt="CA S.K. Mundhra"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -top-4 -left-4 w-full h-full bg-white/10 rounded-2xl -z-10"></div>
                </div>
              </div>

              <div className="md:col-span-3 space-y-6">
                <div>
                  <div className="inline-block mb-3 px-4 py-1 bg-[#1B5E20]/20 backdrop-blur-sm border border-white/30 rounded-full">
                    <span className="text-white text-sm font-medium">Founder & Lead Partner</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    CA S.K. Mundhra
                  </h2>
                  <p className="text-white text-xl font-light">FCA, B.Com | CA Since 1989</p>
                </div>

                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p className="text-lg">
                    A true embodiment of perseverance and dedication, <span className="text-white font-semibold">CA S.K. Mundhra's</span> journey is nothing short of inspirational—a genuine rags-to-riches story built on unwavering principles and relentless hard work.
                  </p>
                  <p className="text-lg">
                    Since qualifying as a Chartered Accountant in <span className="text-white font-semibold">1989</span>, he founded S.K. Mundhra & Co. in <span className="text-white font-semibold">1991</span>, transforming it into one of Odisha's most trusted and respected CA firms over the past 35 years.
                  </p>
                  <p className="text-lg">
                    Even after three and a half decades of practice, he remains the <span className="text-amber-400 font-semibold">first person to enter the office and the last to leave</span>—a testament to his extraordinary commitment and leadership by example.
                  </p>
                  <p className="text-lg">
                    Known throughout the industry for his <span className="text-white font-semibold">impeccable ethical practices</span>, CA Mundhra has built the firm's reputation on the pillars of integrity, accuracy, and client trust. His hands-on approach and deep technical expertise continue to guide every engagement the firm undertakes.
                  </p>
                  <p className="text-lg border-l-4 border-white pl-6 italic">
                    "Excellence is not a destination but a continuous journey of dedication, ethics, and unwavering commitment to our clients."
                  </p>
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <Shield className="h-8 w-8 text-white" />
                  <div>
                    <div className="text-white font-semibold text-lg">Built on Ethics & Trust</div>
                    <div className="text-gray-400">35 years of exemplary service</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Why Choose S.K. Mundhra & Co.
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                A perfect blend of experience, expertise, and ethical excellence
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="group p-8 bg-gradient-to-br from-slate-50 to-green-900/30 rounded-2xl border border-slate-200 hover:border-white/50 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-#2E7D32 to-#1B5E20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">35 Years of Excellence</h3>
                <p className="text-slate-600 leading-relaxed">
                  Over three decades of proven expertise in audit, taxation, and compliance services, serving clients with unwavering dedication.
                </p>
              </div>

              <div className="group p-8 bg-gradient-to-br from-slate-50 to-green-900/30 rounded-2xl border border-slate-200 hover:border-white/50 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-#2E7D32 to-#1B5E20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Experienced Team</h3>
                <p className="text-slate-600 leading-relaxed">
                  Team of 20+ professionals combining decades of experience with fresh perspectives from newly qualified Chartered Accountants.
                </p>
              </div>

              <div className="group p-8 bg-gradient-to-br from-slate-50 to-green-900/30 rounded-2xl border border-slate-200 hover:border-white/50 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-#2E7D32 to-#1B5E20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Ethical Practice</h3>
                <p className="text-slate-600 leading-relaxed">
                  Built on the foundation of integrity and transparency, ensuring every engagement is handled with the highest ethical standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1B5E20] relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience Excellence?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Let us handle your financial compliance with the precision and integrity that defines our 35-year legacy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/919437046671?text=Hello%2C%20I%20would%20like%20to%20schedule%20a%20meeting%20with%20S.K.%20Mundhra%20%26%20Co."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg"
                  className="bg-white text-[#1B5E20] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-xl transition-all duration-300 transform hover:scale-105 font-semibold"
                >
                  Schedule a Meeting
                </Button>
              </a>
              <Button 
                onClick={() => navigate('/services')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg backdrop-blur-sm transition-all duration-300"
              >
                Explore Services
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
